import { useTranslation } from 'react-i18next';
import { useAppContext } from '../context/AppContext';

/**
 * Valida si el usuario tiene permisos para acceder a un componente de perfil consultivo.
 *
 * @param {string} path Ruta al componente en la estructura del perfil del usuario.
 * @param {any} userProfilingConfigParam userProfilingConfigParam correspondientes al state, se pasa cuando se usa useEffects .
 * @param {string} radical Radical del usuario.
 * @returns {boolean} `true` si el usuario tiene permisos, `false` en caso contrario.
 */
export function validateAdvisoryProfile(path: string, userProfilingConfigParam?: any, radical?: string, typePermission: string = ''): boolean {
    let userProfilingLocal: any = null;
    let radicalLocal: string = '';
    if (userProfilingConfigParam) {
        userProfilingLocal = userProfilingConfigParam;
        radicalLocal = radical ?? '';
    } else {
        const { state } = useAppContext();
        const { userProfilingConfig, userProfiling } = state;
        userProfilingLocal = userProfilingConfig;
        radicalLocal = userProfiling?.radicalId
    }
    const keys = path.split('.');
    // Buscamos el objeto correspondiente al radicalLocal
    const objLocal = userProfilingLocal['menu-profiles'].find((profile: { myAndbank: { radical: string; }; }) => profile.myAndbank.radical === radicalLocal);

    // Objeto que se usa para navegar por la estructura del perfil del usuario.
    let currentObj: any = objLocal && objLocal !== undefined ? objLocal?.myAndbank : userProfilingLocal['menu-profiles'][0].myAndbank;

    // Recorre las claves y navega por la estructura del perfil del usuario.
    for (const key of keys) {
        if (currentObj && typeof currentObj === 'object' && key in currentObj) {
            currentObj = currentObj[key];
        } else {
            // console.debug(`ValidateAdvisoryProfile - Path myAndbank.'${path}' does not exist in the provided object user Profiling Config.`);
            return false; // Si la clave no existe, se imprime un mensaje de error y se devuelve `false`.
        }
    }
    // Valida el tipo de dato y el valor en la última clave de la ruta.
    if (typeof currentObj === 'string') {
        // Preguntamos primero si trae un tipo de permiso que desea validar, si lo trae entonces comparamos si es ese el permiso que tiene el usuario
        if (typePermission !== '') {
            if (typePermission === currentObj) {
                return true;
            } else {
                // console.debug(`ValidateAdvisoryProfile - Path myAndbank.'${path}' does not point to a string value equal to 'O' or 'C' is '${currentObj}'.`);
                return false; // Si el valor no es válido, se imprime un mensaje de error y se devuelve `false`.
            }
        } else {
            // Si no se ingreso permiso para validar, entonces realiza la validacion de perfilado normal
            if (currentObj === 'O' || currentObj === 'C' || currentObj === 'L' || currentObj === 'M') {
                return true;
            } else {
                // console.debug(`ValidateAdvisoryProfile - Path myAndbank.'${path}' does not point to a string value equal to 'O' or 'C' is '${currentObj}'.`);
                return false; // Si el valor no es válido, se imprime un mensaje de error y se devuelve `false`.
            }
        }
    } else {
        // console.debug(`ValidateAdvisoryProfile - Path myAndbank.'${path}' does not point to a string value is '${currentObj}'.`);
        return false; // Si el tipo de dato no es válido, se imprime un mensaje de error y se devuelve `false`.
    }
}

/**
 * Valida si el usuario tiene permisos para acceder a un componente de perfil operativo.
 *
 * @param {string} path Ruta al componente en la estructura del perfil del usuario.
 * @param {any} userProfilingConfigParam userProfilingConfigParam correspondientes al state, se pasa cuando se usa useEffects .
 * @param {string} radical Radical del usuario.
 * @returns {boolean} `true` si el usuario tiene permisos, `false` en caso contrario.
 */
export function validateOperationalProfile(path: string, userProfilingConfigParam?: any, radical?: string): boolean {
    let userProfilingLocal: any = null;
    let radicalLocal: string = '';
    if (userProfilingConfigParam) {
        userProfilingLocal = userProfilingConfigParam;
        radicalLocal = radical ?? '';
    } else {
        const { state } = useAppContext();
        const { userProfilingConfig, userProfiling } = state;
        userProfilingLocal = userProfilingConfig;
        radicalLocal = userProfiling?.radicalId
    }
    // Buscamos el objeto correspondiente al radicalLocal
    const objLocal = userProfilingLocal['menu-profiles'].find((profile: { myAndbank: { radical: string; }; }) => profile.myAndbank.radical === radicalLocal);

    // Objeto que se usa para navegar por la estructura del perfil del usuario.
    let currentObj: any = objLocal && objLocal !== undefined ? objLocal?.myAndbank : userProfilingLocal['menu-profiles'][0].myAndbank;
    const keys = path.split('.');

    // Recorre las claves y navega por la estructura del perfil del usuario.
    for (const key of keys) {
        if (currentObj && typeof currentObj === 'object' && key in currentObj) {
            currentObj = currentObj[key];
        } else {
            // console.debug(`ValidateOperationalProfile - Path myAndbank.'${path}' does not exist in the provided object user Profiling Config.`);
            return false; // Si la clave no existe, se imprime un mensaje de error y se devuelve `false`.
        }
    }
    // Valida el tipo de dato y el valor en la última clave de la ruta.
    if (typeof currentObj === 'string') {
        if (currentObj === 'O') {
            return true;
        } else {
            // console.debug(`ValidateOperationalProfile - Path myAndbank.'${path}' does not point to a string value equal to 'O' is '${currentObj}'.`);
            return false; // Si el valor no es válido, se imprime un mensaje de error y se devuelve `false`.
        }
    } else {
        // console.debug(`ValidateOperationalProfile - Path myAndbank.'${path}' does not point to a string value is '${currentObj}'.`);
        return false; // Si el tipo de dato no es válido, se imprime un mensaje de error y se devuelve `false`.
    }
}

/**
 * Valida si el usuario tiene permisos para acceder a un componente de perfil limitado.
 *
 * @param {string} path Ruta al componente en la estructura del perfil del usuario.
 * @param {any} userProfilingConfigParam userProfilingConfigParam correspondientes al state, se pasa cuando se usa useEffects .
 * @param {string} radical Radical del usuario.
 * @returns {boolean} `true` si el usuario tiene permisos, `false` en caso contrario.
 */
export function validateLimitedProfile(path: string, userProfilingConfigParam?: any, radical?: string): boolean {
    let userProfilingLocal: any = null;
    let radicalLocal: string = '';
    if (userProfilingConfigParam) {
        userProfilingLocal = userProfilingConfigParam;
        radicalLocal = radical ?? '';
    } else {
        const { state } = useAppContext();
        const { userProfilingConfig, userProfiling } = state;
        userProfilingLocal = userProfilingConfig;
        radicalLocal = userProfiling?.radicalId
    }
    // Buscamos el objeto correspondiente al radicalLocal
    const objLocal = userProfilingLocal['menu-profiles'].find((profile: { myAndbank: { radical: string; }; }) => profile.myAndbank.radical === radicalLocal);

    // Objeto que se usa para navegar por la estructura del perfil del usuario.
    let currentObj: any = objLocal && objLocal !== undefined ? objLocal?.myAndbank : userProfilingLocal['menu-profiles'][0].myAndbank;
    const keys = path.split('.');

    // Recorre las claves y navega por la estructura del perfil del usuario.
    for (const key of keys) {
        if (currentObj && typeof currentObj === 'object' && key in currentObj) {
            currentObj = currentObj[key];
        } else {
            // console.debug(`ValidateOperationalProfile - Path myAndbank.'${path}' does not exist in the provided object user Profiling Config.`);
            return false; // Si la clave no existe, se imprime un mensaje de error y se devuelve `false`.
        }
    }
    // Valida el tipo de dato y el valor en la última clave de la ruta.
    if (typeof currentObj === 'string') {
        if (currentObj === 'L') {
            return true;
        } else {
            // console.debug(`ValidateOperationalProfile - Path myAndbank.'${path}' does not point to a string value equal to 'O' is '${currentObj}'.`);
            return false; // Si el valor no es válido, se imprime un mensaje de error y se devuelve `false`.
        }
    } else {
        // console.debug(`ValidateOperationalProfile - Path myAndbank.'${path}' does not point to a string value is '${currentObj}'.`);
        return false; // Si el tipo de dato no es válido, se imprime un mensaje de error y se devuelve `false`.
    }
}

/**
 * @param {any} userProfilingConfigParam userProfilingConfigParam correspondientes al state, se pasa cuando se usa useEffects .
 * @param {string} radical Radical del usuario.
 * @returns {boolean} `true` si el usuario tiene permisos, `false` en caso contrario.
 */
export function isKidsOrJunior(userProfilingConfigParam?: any, radical?: string): boolean {
    let userProfilingLocal: any = null;
    let radicalLocal: string = '';
    if (userProfilingConfigParam) {
        userProfilingLocal = userProfilingConfigParam;
        radicalLocal = radical ?? '';
    } else {
        const { state } = useAppContext();
        const { userProfilingConfig, userProfiling } = state;
        userProfilingLocal = userProfilingConfig;
        radicalLocal = userProfiling?.radicalId
    }
    // Buscamos el objeto correspondiente al radicalLocal
    const objLocal = userProfilingLocal['menu-profiles'].find((profile: { myAndbank: { radical: string; }; }) => profile.myAndbank.radical === radicalLocal);

    // Objeto que se usa para navegar por la estructura del perfil del usuario.
    const currentObj: any = objLocal && objLocal !== undefined ? objLocal?.myAndbank : userProfilingLocal['menu-profiles'][0].myAndbank;

    if (currentObj['application-theme'] === 'KID' || currentObj['application-theme'] === 'KIDS' || currentObj['application-theme'] === 'JUN') {
        return true
    } else {
        return false
    }
}

/**
 * @param {any} userProfilingConfigParam userProfilingConfigParam correspondientes al state, se pasa cuando se usa useEffects .
 * @param {string} radical Radical del usuario.
 * @returns {boolean} `true` si el usuario tiene permisos, `false` en caso contrario.
 */
export function isKids(userProfilingConfigParam?: any, radical?: string): boolean {
    let userProfilingLocal: any = null;
    let radicalLocal: string = '';
    if (userProfilingConfigParam) {
        userProfilingLocal = userProfilingConfigParam;
        radicalLocal = radical ?? '';
    } else {
        const { state } = useAppContext();
        const { userProfilingConfig, userProfiling } = state;
        userProfilingLocal = userProfilingConfig;
        radicalLocal = userProfiling?.radicalId
    }
    // Buscamos el objeto correspondiente al radicalLocal
    const objLocal = userProfilingLocal['menu-profiles'].find((profile: { myAndbank: { radical: string; }; }) => profile.myAndbank.radical === radicalLocal);

    // Objeto que se usa para navegar por la estructura del perfil del usuario.
    const currentObj: any = objLocal && objLocal !== undefined ? objLocal?.myAndbank : userProfilingLocal['menu-profiles'][0].myAndbank;

    if (currentObj['application-theme'] === 'KID' || currentObj['application-theme'] === 'KIDS') {
        return true
    } else {
        return false
    }
}

/**
 * Valida si el usuario tiene permisos para acceder a un componente de perfil operativo.
 *
 * @param {any} userProfilingConfigParam userProfilingConfigParam correspondientes al state, se pasa cuando se usa useEffects .
 * @param {string} radical Radical del usuario.
 * @returns {string} `true` si el usuario tiene permisos, `false` en caso contrario.
 */
export function descriptionProfile( userProfilingConfigParam?: any, radical?: string, onlyPerilId: boolean = false): string {
    const { t } = useTranslation();
    let userProfilingLocal: any = null;
    let radicalLocal: string = '';
    if (userProfilingConfigParam) {
        userProfilingLocal = userProfilingConfigParam;
        radicalLocal = radical ?? '';
    } else {
        const { state } = useAppContext();
        const { userProfilingConfig, userProfiling } = state;
        userProfilingLocal = userProfilingConfig;
        radicalLocal = userProfiling?.radicalId
    }
    // Buscamos el objeto correspondiente al radicalLocal
    const objLocal = userProfilingLocal['menu-profiles'].find((profile: { myAndbank: { radical: string; }; }) => profile.myAndbank.radical === radicalLocal);

    // Objeto que se usa para navegar por la estructura del perfil del usuario.
    const currentObj: any = objLocal && objLocal !== undefined ? objLocal?.myAndbank : userProfilingLocal['menu-profiles'][0].myAndbank;
    if (onlyPerilId) {
        return currentObj?.perfilId;
    }else{
        // return currentObj?.perfilId + ' (' + t(currentObj?.perfilDesc) + ')';
        return t(currentObj?.perfilDesc);
    }
}

/**
 * @param {any} userProfilingConfigParam userProfilingConfigParam correspondientes al state, se pasa cuando se usa useEffects .
 * @param {string} radical Radical del usuario.
 * @returns {boolean} `true` si el usuario es Power User (perfilId === "2000099"), `false` en caso contrario.
 */
export function isPowerUser(userProfilingConfigParam?: any, radical?: string): boolean {
    let userProfilingLocal: any = null;
    let radicalLocal: string = '';
    if (userProfilingConfigParam) {
        userProfilingLocal = userProfilingConfigParam;
        radicalLocal = radical ?? '';
    } else {
        const { state } = useAppContext();
        const { userProfilingConfig, userProfiling } = state;
        userProfilingLocal = userProfilingConfig;
        radicalLocal = userProfiling?.radicalId
    }
    // Buscamos el objeto correspondiente al radicalLocal
    const objLocal = userProfilingLocal['menu-profiles'].find((profile: { myAndbank: { radical: string; }; }) => profile.myAndbank.radical === radicalLocal);

    // Objeto que se usa para navegar por la estructura del perfil del usuario.
    const currentObj: any = objLocal && objLocal !== undefined ? objLocal?.myAndbank : userProfilingLocal['menu-profiles'][0].myAndbank;

    return currentObj?.perfilId === '2000099';
}